import Grid from '@mui/material/Grid';
import { Box, Paper, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CABINCAST, CABINCAST_AUDIOFILES } from '../../../common/paths';
import AudioFilesGrid from './AudioFilesGrid';
import AudioFileUpload from './AudioFileUpload';
import AudioFileUploadDialog from './AudioFileUploadDialog';
import { AudioFileItem } from '@aviation/cabinapp-cabincast-sdk';
import { useFetch } from '../../../hooks/useFetch';
import AudioFileCategoryChart from './AudioFileCategoryChart';
import { AUDIO_FILE_CATEGORIES } from './AudioFile';

function AudioFiles() {
    const navigate = useNavigate();
    const [tabIndex, setTabIndex] = useState(0); // 0 = All
    const [search, setSearch] = useState('');
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);

    const { status, data } = useFetch<Array<AudioFileItem>>(
        '/api/audiofiles/',
        (response) => Array.isArray(response) ? response : (response?.Items ?? []),
        refreshCounter);

    const category = tabIndex === 0 ? undefined : AUDIO_FILE_CATEGORIES[tabIndex - 1].value;

    const handleFilesSelected = (files: File[]) => {
        setSelectedFiles(files);
        setDialogOpen(true);
    };

    const handleSaved = () => {
        setDialogOpen(false);
        setSelectedFiles([]);
        setRefreshCounter(c => c + 1);
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, paddingBottom: 0, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant='subtitle1' component='h2' sx={{ textTransform: 'uppercase' }}>
                            Audio Files
                        </Typography>
                    </Box>
                    <Tabs value={tabIndex} onChange={(_, value) => setTabIndex(value)} variant='scrollable' scrollButtons='auto'>
                        <Tab label='All' />
                        {AUDIO_FILE_CATEGORIES.map(c => <Tab key={c.value} label={c.label} />)}
                    </Tabs>
                </Paper>
            </Grid>
            <Grid item xs={12} md={8} lg={9}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2 }} elevation={3}>
                            <TextField fullWidth size='small' placeholder='Search' value={search} onChange={e => setSearch(e.target.value)} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: '100px' }} elevation={3}>
                            <AudioFilesGrid category={category} search={search} data={data} status={status}
                                onEdit={(file) => navigate(`/${CABINCAST}/${CABINCAST_AUDIOFILES}/${encodeURIComponent(file.Id!)}`)} />
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Paper sx={{ p: 2 }} elevation={3}>
                        <AudioFileUpload onFilesSelected={handleFilesSelected} />
                    </Paper>
                    <AudioFileCategoryChart
                        data={data}
                        selected={category}
                        onSelect={(c) => setTabIndex(AUDIO_FILE_CATEGORIES.findIndex(o => o.value === c) + 1)}
                    />
                </Box>
            </Grid>
            <AudioFileUploadDialog open={dialogOpen} files={selectedFiles} onClose={() => setDialogOpen(false)} onSaved={handleSaved} />
        </Grid>
    );
}

export default AudioFiles;
